import React, { useState, useEffect, useMemo } from 'react';
import { Search, ArrowLeft } from 'lucide-react';

const HUB_TITLES = {
  services: 'Services',
  learn: 'Learn',
  volunteer: 'Volunteer',
  give: 'Give',
  dialysis: 'Dialysis Centres',
  casket: 'Casket Packages'
};

// Hubs backed by a plain collection of offerings all render through
// OfferingHub / OfferingDetail; only the copy and the CTA differ.
const OFFERING_HUBS = {
  dialysis: {
    heading: 'Our Centres',
    heroTitle: 'Dialysis, Close to Home',
    heroSubtitle: 'Three centres across the island, with subsidies for those who need them.',
    heroImage: '/images/ihsan-care-roadshow.png',
    heroColor: '#0F9488',
    ctaLabel: 'Register at this Centre',
    ctaSuccessLabel: 'Registered',
    confirm: {
      title: "You're registered",
      subtitle: 'Our centre team will call you to confirm a shift and complete your assessment.',
      nextSteps: [
        'A care coordinator calls you within 2 working days.',
        'Bring your latest renal report to your first session.',
        'Our medical social worker completes your subsidy assessment on site.'
      ]
    },
    chatSubtitle: 'MTFA Dialysis Team',
    footnote: 'Subsidy levels are assessed by our medical social workers after your first visit.'
  },
  casket: {
    heading: 'Our Packages',
    heroTitle: 'Ihsan Casket',
    heroSubtitle: 'Respectful Muslim burial services, arranged end to end, 24/7.',
    heroImage: '/images/mtfa-ihsan-casket.jpg',
    heroColor: '#1E3A5F',
    ctaLabel: 'Request this Package',
    ctaSuccessLabel: 'Request Sent',
    confirm: {
      title: 'Request received',
      subtitle: 'Our team has been notified and will contact you within the next 2 hours.',
      nextSteps: [
        'An Ihsan Casket officer calls you to confirm the details.',
        'We collect the deceased from home, hospital or mortuary.',
        'Ghusl, kafan and burial arrangements are handled by our team.'
      ]
    },
    chatSubtitle: 'Ihsan Casket Team',
    footnote: 'For an immediate death report, call our 24-hour line on 9123 4567.'
  },
  give: {
    heading: 'Ways to Give',
    heroTitle: 'Every Gift Counts',
    heroSubtitle: 'Give once, or build a habit — daily, weekly or monthly.',
    heroImage: '/images/hero-slide-2-everyday-ihsan.jpeg',
    heroColor: '#D4A843',
    ctaLabel: 'Continue to Payment',
    ctaSuccessLabel: 'Continue',
    chatSubtitle: 'IhsanOne Giving Team',
    footnote: 'All donations to MTFA qualify for 2.5× tax deduction.'
  }
};

// Detail-page settings per collection. Every card in the app — service, course,
// opportunity, centre, package or cause — opens one of these and commits
// through a sign-in-gated button at the bottom, so the behaviour never varies
// with where the card was tapped. The four core services carry their own CTA
// wording on the item itself, since each one leads into a different flow.
const DETAIL_CONFIG = {
  ...OFFERING_HUBS,
  services: {
    chatSubtitle: 'IhsanOne Services Team',
    confirm: {
      title: 'Sign-in confirmed',
      subtitle: "Let's continue with your request."
    }
  }
};

// "Track My Application" needs an application to track; this stands in for one
// until there's a real backend behind the assistance form.
const TRACKED_APPLICATION = {
  fullName: 'Sarah A.',
  assistanceType: 'Financial Aid',
  submittedAt: new Date('2026-08-12T10:15:00+08:00'),
  trackingLink: 'https://ihsanone.sg/track/IA-2026-0812'
};
import QuickShortcuts from '../../components/QuickShortcuts';
import LearnMoreBanner from '../../components/LearnMoreBanner';
import {
  services, featuredForYou, popularCategories, exploreCategories
} from '../../data/mockData';
import { useOfferings } from '../../components/OfferingsProvider';
import OfferingCard from '../../components/OfferingCard';
import OfferingHub from '../OfferingHub';
import OfferingDetail from '../OfferingDetail';
import StripeCheckout from '../StripeCheckout';
import EngagementConfirmation from '../EngagementConfirmation';
import { loadSavedItems, toggleSavedItem } from '../../utils/saved';
import { requireAuth } from '../../components/AuthGate';

// Import Ihsan Casket components
import LandingPage from '../LandingPage';
import BookingWizard from '../BookingWizard';
import TrackerView from '../TrackerView';
import MyPastBurials from '../MyPastBurials';
import MyPastBurialDetail from '../MyPastBurialDetail';
import MyAttendedBurials from '../MyAttendedBurials';
import RequestAssistance from '../RequestAssistance';
import AssistanceTracker from '../AssistanceTracker';
import VolunteerHub from './VolunteerHub';
import VolunteerDetail from '../VolunteerDetail';
import LearnHub from './LearnHub';
import ServicesHub from './ServicesHub';
import UlumIhsanCourse from '../UlumIhsanCourse';
import ServiceNeeds from '../ServiceNeeds';
import UlumAsatizahForm from '../UlumAsatizahForm';
import DailyStudentAttendance from '../DailyStudentAttendance';

function searchHaystack(item, extra = '') {
  return [
    item.title,
    item.org,
    item.tag,
    item.date,
    item.description,
    item.location,
    item.ctaLabel,
    extra,
    ...(item.highlights || []),
    ...(item.facts || []).flatMap((f) => [f.label, f.value])
  ].filter(Boolean).join(' ').toLowerCase();
}

function matchesQuery(haystack, tokens) {
  return tokens.every((token) => haystack.includes(token));
}

export default function Services({
  onNavigate,
  activeTopTab = 'all',
  onTopTabChange,
  onOpenChatInbox,
  pendingChatTarget,
  onConsumePendingChatTarget,
  pendingExploreItem,
  onConsumePendingExploreItem,
  // The search field lives in the shell's top bar, so its text is owned there
  // and handed down — Home is one of several screens sharing that one bar.
  searchQuery = '',
  onSearchChange,
  onOpenRewards,
  onScheduleGiving
}) {
  const [activeService, setActiveService] = useState(null);
  const [ihsanView, setIhsanView] = useState('landing');
  const [bookingData, setBookingData] = useState(null);
  const [shareableLink, setShareableLink] = useState(null);
  const [selectedPastBurial, setSelectedPastBurial] = useState(null);
  const [assistanceView, setAssistanceView] = useState('form'); // 'form' | 'tracker'
  const [assistanceData, setAssistanceData] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);
  const [savedIds, setSavedIds] = useState(() => loadSavedItems());
  const [activeCategory, setActiveCategory] = useState(popularCategories[0].id);
  const [volunteerFlowView, setVolunteerFlowView] = useState('hub'); // 'hub' | 'signup' | 'detail'
  const [selectedVolunteerOpp, setSelectedVolunteerOpp] = useState(null);
  const [learnFlowView, setLearnFlowView] = useState('hub'); // 'hub' | 'auth' | 'course' | 'detail'
  const [selectedLearnItem, setSelectedLearnItem] = useState(null);
  const [chatEntryIsInbox, setChatEntryIsInbox] = useState(false);
  // Generic service intake: { id, title, description }
  const [serviceFlow, setServiceFlow] = useState(null);
  // The card currently opened on a shared detail page, as { type, item }.
  // Covers services, dialysis centres, casket packages and ways to give.
  const [selectedOffering, setSelectedOffering] = useState(null);
  // Giving option being paid for, once the donor clears the sign-in gate.
  const [checkoutOption, setCheckoutOption] = useState(null);
  // Closing screen shown after any completed engagement.
  const [confirmation, setConfirmation] = useState(null);
  const {
    heroSlides,
    serviceHubItems,
    volunteerHubItems,
    learnHubItems,
    detailCollections
  } = useOfferings();

  // Popular Services browses the same content as the hubs.
  const POPULAR_SOURCES = {
    services: serviceHubItems,
    learn: learnHubItems,
    volunteer: volunteerHubItems,
    dialysis: detailCollections.dialysis,
    casket: detailCollections.casket,
    give: detailCollections.give
  };

  const searchGroups = useMemo(() => {
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];

    const pick = (items, extra = '') =>
      (items || []).filter((item) => matchesQuery(searchHaystack(item, extra), tokens));

    const categoryItems = exploreCategories
      .filter((cat) => {
        const extra = cat.id === 'dialysis' ? 'kidney care haemodialysis renal' : '';
        return matchesQuery(
          `${cat.label} ${cat.subtitle} ${extra}`.toLowerCase(),
          tokens
        );
      })
      .map((cat) => ({
        id: `category-${cat.id}`,
        title: cat.label,
        org: cat.subtitle,
        image: cat.image,
        tag: 'Category',
        tagColor: 'bg-slate-700',
        date: 'Browse all',
        category: cat
      }));

    return [
      { key: 'categories', label: 'Categories', collection: 'category', items: categoryItems },
      { key: 'services', label: 'Services', collection: 'services', items: pick(serviceHubItems) },
      { key: 'learn', label: 'Learning', collection: 'learn', items: pick(learnHubItems) },
      { key: 'volunteer', label: 'Community', collection: 'volunteer', items: pick(volunteerHubItems) },
      {
        key: 'dialysis',
        label: 'Dialysis',
        collection: 'dialysis',
        items: pick(detailCollections.dialysis, 'kidney care haemodialysis renal')
      },
      { key: 'casket', label: 'Casket', collection: 'casket', items: pick(detailCollections.casket) },
      { key: 'give', label: 'Give', collection: 'give', items: pick(detailCollections.give) }
    ].filter((group) => group.items.length > 0);
  }, [searchQuery, serviceHubItems, learnHubItems, volunteerHubItems, detailCollections]);

  useEffect(() => {
    if (!pendingChatTarget) return;
    const { id, type } = pendingChatTarget;
    if (type === 'volunteer') {
      const opp = volunteerHubItems.find((o) => o.id === id);
      if (opp) {
        setSelectedVolunteerOpp(opp);
        setVolunteerFlowView('detail');
        setChatEntryIsInbox(true);
      }
    } else if (type === 'learn') {
      const item = learnHubItems.find((o) => o.id === id);
      if (item) {
        setSelectedLearnItem(item);
        setLearnFlowView('detail');
        setChatEntryIsInbox(true);
      }
    } else if (detailCollections[type]) {
      const item = detailCollections[type].find((o) => o.id === id);
      if (item) {
        setSelectedOffering({ type, item });
        setChatEntryIsInbox(true);
      }
    }
    onConsumePendingChatTarget?.();
  }, [pendingChatTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Saved cards (and any other tab) reopen the same detail page Explore would,
  // so the gated bottom CTA is never skipped just because the card lived elsewhere.
  useEffect(() => {
    if (!pendingExploreItem) return;
    const { type, item } = pendingExploreItem;
    setChatEntryIsInbox(false);
    if (type === 'volunteer') {
      setSelectedVolunteerOpp(item);
      setVolunteerFlowView('detail');
    } else if (type === 'learn') {
      setSelectedLearnItem(item);
      setLearnFlowView('detail');
    } else {
      setSelectedOffering({ type, item });
    }
    onConsumePendingExploreItem?.();
  }, [pendingExploreItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Entering a full-page sub-view keeps the scroller where it was, which lands
  // the user on blank space part-way down the new page. Reset it on every
  // page-level change.
  useEffect(() => {
    document.querySelector('main')?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [serviceFlow?.id, activeService, volunteerFlowView, learnFlowView, activeTopTab,
      selectedOffering?.item.id, checkoutOption?.id]);

  useEffect(() => {
    // Only auto-rotate while the Explore directory is visible
    if (activeService) return undefined;
    if (!heroSlides.length) return undefined;
    const t = setInterval(() => {
      setHeroIdx((i) => (i + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(t);
  }, [activeService, heroSlides.length]);

  // Ihsan Casket and Financial Assistance own bespoke flows. Everything else
  // funnels through sign-up/login into the generic needs-and-preferences step.
  // Tapping a service card opens its detail page, exactly like every other
  // card. Nothing is committed until the gated button at the bottom.
  const handleServiceClick = (serviceId) => {
    const item = serviceHubItems.find((s) => s.id === serviceId);
    if (!item) {
      startServiceFlow(serviceId);
      return;
    }
    setChatEntryIsInbox(false);
    setSelectedOffering({ type: 'services', item });
  };

  // Runs only once the user is signed in, from the detail page's CTA.
  const startServiceFlow = (serviceId) => {
    if (serviceId === 'ihsan-casket') {
      setActiveService('ihsan-casket');
      setIhsanView('landing');
      return;
    }
    if (serviceId === 'financial-assistance') {
      setActiveService('financial-assistance');
      setAssistanceView('form');
      return;
    }
    if (serviceId === 'kidney-care') {
      handleTopTabClick('dialysis');
      return;
    }
    if (serviceId === 'ulum-asatizah') {
      setServiceFlow({
        id: 'ulum-asatizah',
        title: 'Ulum Ihsan Asatizah',
        description: 'For administration of Ulum Ihsan classes'
      });
      return;
    }
    if (serviceId === 'daily-attendance') {
      setServiceFlow({
        id: 'daily-attendance',
        title: 'Daily Student Attendance',
        description: 'For duty officers logging daily programme and movement'
      });
      return;
    }
    const svc = serviceHubItems.find((s) => s.id === serviceId)
      || services.find((s) => s.id === serviceId);
    setServiceFlow({
      id: serviceId,
      title: svc?.title || 'Register your interest',
      description: svc?.description || ''
    });
  };

  // Financial Assistance keeps its own application form; jumping straight to the
  // tracker needs a submitted application, so seed one for the demo.
  const handleTrackApplication = async () => {
    if (!(await requireAuth())) return;
    setActiveService('financial-assistance');
    setAssistanceData(TRACKED_APPLICATION);
    setAssistanceView('tracker');
  };

  // "Other Ways" tiles get the same detail page + gated bottom CTA as every
  // other card, then continue into the generic needs form once the user is in.
  const handleExpressInterest = (id, title) => {
    setChatEntryIsInbox(false);
    setSelectedOffering({
      type: 'services',
      item: {
        id,
        title,
        org: 'IhsanOne',
        date: 'Register your interest',
        image: '/images/hero-slide-1-community.jpeg',
        tag: 'Interest',
        tagColor: 'bg-primary',
        ctaLabel: 'Register Interest',
        ctaSuccessLabel: 'Continue',
        description: `Tell us a little about your interest in ${title} so we can match you to the right programme.`,
        facts: [
          { label: 'Response', value: '3 working days' },
          { label: 'Cost', value: 'Free' },
          { label: 'Format', value: 'Short form' },
          { label: 'Follow-up', value: 'Chat' }
        ],
        highlights: [
          'Takes about two minutes after you sign in.',
          'A coordinator follows up through chat.',
          'No commitment until you confirm.'
        ]
      }
    });
  };

  // Featured cards open the real listing they point at, not just its hub —
  // same detail page, same gated button, whether you came from Featured or
  // from Popular Services.
  const handleOpenFeatured = (item) => {
    setChatEntryIsInbox(false);
    if (item.linkType === 'learn') {
      const target = learnHubItems.find((i) => i.id === item.linkId);
      if (!target) return;
      setSelectedLearnItem(target);
      setLearnFlowView('detail');
      return;
    }
    const target = volunteerHubItems.find((i) => i.id === item.linkId);
    if (!target) return;
    setSelectedVolunteerOpp(target);
    setVolunteerFlowView('detail');
  };

  const handleCategoryTileClick = (cat) => {
    if (cat.target.type === 'tab') {
      handleTopTabClick(cat.target.value);
      return;
    }
    handleServiceClick(cat.target.value);
  };

  const handleTopTabClick = (tabId) => {
    onTopTabChange?.(tabId);
    setSelectedOffering(null);
    setSelectedVolunteerOpp(null);
    setSelectedLearnItem(null);
    setVolunteerFlowView('hub');
    setLearnFlowView('hub');
    setChatEntryIsInbox(false);
  };

  // Cards open their detail page straight away; the sign-in gate now fires from
  // the commit action inside (Join / Enroll), not from the card tap.
  const handleSelectVolunteerOpportunity = (opportunity) => {
    setSelectedVolunteerOpp(opportunity);
    setVolunteerFlowView('detail');
    setChatEntryIsInbox(false);
  };

  const handleSelectLearnItem = (item) => {
    setSelectedLearnItem(item);
    setLearnFlowView('detail');
    setChatEntryIsInbox(false);
  };

  const handleBackToInboxFromVolunteer = () => {
    setVolunteerFlowView('hub');
    setSelectedVolunteerOpp(null);
    onOpenChatInbox?.();
  };

  const handleBackToInboxFromLearn = () => {
    setLearnFlowView('hub');
    setSelectedLearnItem(null);
    onOpenChatInbox?.();
  };

  const handleBackToInboxFromOffering = () => {
    setSelectedOffering(null);
    onOpenChatInbox?.();
  };

  // A Popular Services card opens the same detail page its hub would have
  // opened — without leaving the Explore directory — so the gated button at
  // the bottom is always the next step, wherever the card was tapped.
  const openPopularItem = (collection, item) => {
    setChatEntryIsInbox(false);
    if (collection === 'learn') {
      setSelectedLearnItem(item);
      setLearnFlowView('detail');
    } else if (collection === 'volunteer') {
      setSelectedVolunteerOpp(item);
      setVolunteerFlowView('detail');
    } else {
      setSelectedOffering({ type: collection, item });
    }
  };

  const handleSearchResultClick = (collection, item) => {
    if (collection === 'category') {
      onSearchChange?.('');
      handleCategoryTileClick(item.category);
      return;
    }
    openPopularItem(collection, item);
  };

  const handleToggleSave = (id) => {
    setSavedIds(toggleSavedItem(id));
  };

  const handleIhsanStartBooking = () => {
    setIhsanView('booking');
  };

  const handleIhsanViewTracker = (link) => {
    setShareableLink(link || 'funeral-mr-razak');
    setIhsanView('tracker');
  };

  const handleIhsanBookingComplete = (data) => {
    setBookingData(data);
    const link = `funeral-${data.deceasedName.toLowerCase().replace(/\s+/g, '-')}`;
    setShareableLink(link);
  };

  const handleIhsanBack = () => {
    setIhsanView('landing');
  };

  const handleViewPastBurials = () => setIhsanView('past-burials');
  const handleViewAttendedBurials = () => setIhsanView('attended-burials');
  const handleOpenPastDetail = (entry) => {
    setSelectedPastBurial(entry);
    setIhsanView('past-burial-detail');
  };
  const handleBackToPastBurials = () => setIhsanView('past-burials');

  const handleBackToServices = () => {
    setActiveService(null);
    setIhsanView('landing');
  };

  // Closing screen for a completed engagement, shown over everything else.
  if (confirmation) {
    return (
      <EngagementConfirmation
        {...confirmation}
        primaryLabel={confirmation.primaryLabel || 'Done'}
        onPrimary={() => setConfirmation(null)}
        secondaryLabel="Message the team"
        onSecondary={() => { setConfirmation(null); onOpenChatInbox?.(); }}
      />
    );
  }

  // If Financial Assistance is active, show the Request Assistance form or tracker
  if (activeService === 'financial-assistance') {
    if (assistanceView === 'tracker' && assistanceData) {
      return (
        <AssistanceTracker
          applicant={assistanceData}
          trackingLink={assistanceData.trackingLink}
          onBack={handleBackToServices}
        />
      );
    }
    return (
      <RequestAssistance
        onBack={handleBackToServices}
        onViewTracker={(data) => {
          setAssistanceData(data);
          setAssistanceView('tracker');
        }}
      />
    );
  }

  // If Ihsan Casket is active, show its full interface
  if (activeService === 'ihsan-casket') {
    return (
      <div className="min-h-screen">
        {/* Back Button */}
        <div className="bg-card border-b px-4 py-3">
          <button
            onClick={handleBackToServices}
            className="text-primary hover:underline font-medium"
          >
            ← Back to Explore
          </button>
        </div>

        {/* Ihsan Casket Views */}
        {ihsanView === 'landing' && (
          <LandingPage
            onStartBooking={handleIhsanStartBooking}
            onViewTracker={handleIhsanViewTracker}
            onViewPastBurials={handleViewPastBurials}
            onViewAttendedBurials={handleViewAttendedBurials}
          />
        )}
        {ihsanView === 'booking' && (
          <BookingWizard
            onComplete={handleIhsanBookingComplete}
            onBack={handleIhsanBack}
          />
        )}
        {ihsanView === 'tracker' && (
          <TrackerView
            shareableLink={shareableLink}
            bookingData={bookingData}
            onBack={handleIhsanBack}
          />
        )}
        {ihsanView === 'past-burials' && (
          <MyPastBurials
            onBack={handleIhsanBack}
            onOpenDetail={handleOpenPastDetail}
          />
        )}
        {ihsanView === 'past-burial-detail' && (
          <MyPastBurialDetail
            entry={selectedPastBurial}
            onBack={handleBackToPastBurials}
          />
        )}
        {ihsanView === 'attended-burials' && (
          <MyAttendedBurials onBack={handleIhsanBack} />
        )}
      </div>
    );
  }

  // Generic service intake — needs & preferences, gated on submit
  if (serviceFlow?.id === 'daily-attendance') {
    return (
      <DailyStudentAttendance onBack={() => setServiceFlow(null)} />
    );
  }

  if (serviceFlow?.id === 'ulum-asatizah') {
    return (
      <UlumAsatizahForm
        onBack={() => setServiceFlow(null)}
        onDone={(session) => {
          const [y, m, d] = (session.date || '').split('-');
          const dateLabel = d ? `${Number(d)}/${Number(m)}/${y}` : session.date;
          setConfirmation({
            title: 'Session logged',
            subtitle: 'This record is on the timesheet queue. Finance will use it for your monthly payment.',
            itemTitle: session.classTitle,
            itemImage: '/images/ulum-ihsan-dua-surah-hero.jpeg',
            meta: [
              { label: 'Date', value: dateLabel },
              { label: 'Time', value: `${session.startLabel} · ${session.duration} hour${session.duration === 1 ? '' : 's'}` },
              { label: 'Host', value: session.hostName },
              { label: 'Present', value: `${session.presentCount} of ${session.rosterSize}` }
            ],
            nextSteps: [
              `${session.lessonTitle} is saved against this session.`,
              'Log another class if you taught more than one today.',
              'At month-end this feeds the timesheet you sign for finance.'
            ],
            primaryLabel: 'Back to Services'
          });
          setServiceFlow(null);
        }}
      />
    );
  }

  if (serviceFlow) {
    return (
      <ServiceNeeds
        key={serviceFlow.id}
        serviceId={serviceFlow.id}
        title={serviceFlow.title}
        description={serviceFlow.description}
        onBack={() => setServiceFlow(null)}
        onDone={() => {
          setConfirmation({
            title: 'Application submitted',
            subtitle: 'Your preferences are with our team and your reference number is on its way.',
            itemTitle: serviceFlow.title,
            meta: [
              { label: 'Reference', value: `IH-${serviceFlow.id.slice(0, 3).toUpperCase()}-4821` },
              { label: 'Status', value: 'Under review' }
            ],
            nextSteps: [
              'A case officer reviews your submission within 3 working days.',
              'We may ask for supporting documents through the chat.',
              'You will be notified here once a decision is made.'
            ]
          });
          setServiceFlow(null);
        }}
      />
    );
  }

  // Giving checkout — reached only after the donor clears the sign-in gate
  if (checkoutOption) {
    return (
      <StripeCheckout
        key={checkoutOption.id}
        option={checkoutOption}
        onBack={() => setCheckoutOption(null)}
        onDone={({ amount }) => {
          setConfirmation({
            title: 'Jazakallah khair',
            subtitle: 'Your donation has gone through. May Allah accept it from you.',
            itemTitle: checkoutOption.title,
            itemImage: checkoutOption.image,
            meta: [
              { label: 'Amount', value: `$${amount.toLocaleString()}` },
              { label: 'Receipt', value: 'DN-2026-10482' },
              { label: 'Tax deduction', value: '2.5× eligible' }
            ],
            nextSteps: [
              'Your tax-deductible receipt is emailed within 24 hours.',
              'We report where your donation went every quarter.',
              'Your Ihsan Points have been credited.'
            ],
            primaryLabel: 'Back to Give'
          });
          setCheckoutOption(null);
          setSelectedOffering(null);
        }}
      />
    );
  }

  // Services / dialysis centres / casket packages / ways to give — one shared
  // detail page, one sign-in-gated button at the bottom.
  if (selectedOffering) {
    const { type, item } = selectedOffering;
    const cfg = DETAIL_CONFIG[type] || {};
    return (
      <OfferingDetail
        key={item.id}
        item={item}
        savedIds={savedIds}
        onToggleSave={handleToggleSave}
        onBack={() => { setSelectedOffering(null); setChatEntryIsInbox(false); }}
        ctaLabel={item.ctaLabel || cfg.ctaLabel}
        ctaSuccessLabel={item.ctaSuccessLabel || cfg.ctaSuccessLabel}
        chatSubtitle={cfg.chatSubtitle}
        initialChatEntry={chatEntryIsInbox ? 'inbox' : null}
        onBackToInbox={handleBackToInboxFromOffering}
        onCtaComplete={() => {
          // Giving collects payment next; the four core services each hand off
          // to their own intake flow. Everything else is done at this point.
          if (type === 'give') {
            setCheckoutOption(item);
            return;
          }
          if (type === 'services') {
            setSelectedOffering(null);
            startServiceFlow(item.id);
            return;
          }
          setConfirmation({
            ...cfg.confirm,
            itemTitle: item.title,
            itemImage: item.image,
            meta: [
              { label: item.org ? 'Provider' : 'Package', value: item.org || item.title },
              { label: 'Reference', value: `IH-${item.id.slice(0, 3).toUpperCase()}-7305` }
            ]
          });
          setSelectedOffering(null);
        }}
      />
    );
  }

  // Volunteer / Learn details render whenever a card is open, not only when
  // that hub's top-tab is active — otherwise Popular Services and Featured
  // cards would skip the gated button and dump the user onto the hub.
  if (volunteerFlowView === 'detail' && selectedVolunteerOpp) {
    return (
      <VolunteerDetail
        key={selectedVolunteerOpp.id}
        kind="volunteer"
        opportunity={selectedVolunteerOpp}
        savedIds={savedIds}
        onToggleSave={handleToggleSave}
        onBack={() => {
          setVolunteerFlowView('hub');
          setSelectedVolunteerOpp(null);
          setChatEntryIsInbox(false);
        }}
        initialChatEntry={chatEntryIsInbox ? 'inbox' : null}
        onBackToInbox={handleBackToInboxFromVolunteer}
        onJoinComplete={() => {
          setConfirmation({
            title: "You're in",
            subtitle: 'Your place is confirmed. The organiser will be in touch with the final briefing.',
            itemTitle: selectedVolunteerOpp.title,
            itemImage: selectedVolunteerOpp.image,
            meta: [
              { label: 'Organiser', value: selectedVolunteerOpp.org },
              { label: 'When', value: selectedVolunteerOpp.date },
              { label: 'Where', value: selectedVolunteerOpp.location }
            ],
            nextSteps: [
              'A calendar invite is on its way to your email.',
              'The organiser sends a briefing 3 days before.',
              'Ihsan Points are credited once you check in on the day.'
            ],
            primaryLabel: 'Back to Volunteer'
          });
          setVolunteerFlowView('hub');
          setSelectedVolunteerOpp(null);
        }}
      />
    );
  }

  // Learn flow — Ulum Ihsan mini-LMS, or generic course detail
  if (learnFlowView === 'course') {
    return <UlumIhsanCourse onExit={() => { setLearnFlowView('hub'); setSelectedLearnItem(null); }} />;
  }
  if (learnFlowView === 'detail' && selectedLearnItem) {
    return (
      <VolunteerDetail
        key={selectedLearnItem.id}
        kind="learn"
        opportunity={selectedLearnItem}
        savedIds={savedIds}
        onToggleSave={handleToggleSave}
        onBack={() => {
          setLearnFlowView('hub');
          setSelectedLearnItem(null);
          setChatEntryIsInbox(false);
        }}
        participantLabel="Participants"
        joinButtonLabel="Enroll in this Course"
        initialChatEntry={chatEntryIsInbox ? 'inbox' : null}
        onBackToInbox={handleBackToInboxFromLearn}
        onJoinComplete={() => {
          // Ulum Ihsan has a mini-LMS behind it; enrolling should drop the
          // learner straight into it rather than onto a confirmation screen.
          if (selectedLearnItem.isLMS) {
            setLearnFlowView('course');
            return;
          }
          setConfirmation({
            title: "You're enrolled",
            subtitle: 'Your seat is reserved. Joining details will reach you before the first session.',
            itemTitle: selectedLearnItem.title,
            itemImage: selectedLearnItem.image,
            meta: [
              { label: 'Provider', value: selectedLearnItem.org },
              { label: 'Starts', value: selectedLearnItem.date },
              { label: 'Where', value: selectedLearnItem.location }
            ],
            nextSteps: [
              'Your confirmation email includes the venue and materials list.',
              'A reminder goes out 2 days before the first session.',
              'Ask the trainer anything through the chat in the meantime.'
            ],
            primaryLabel: 'Back to Learn'
          });
          setLearnFlowView('hub');
          setSelectedLearnItem(null);
        }}
      />
    );
  }

  const activeCollection =
    popularCategories.find((c) => c.id === activeCategory)?.collection || 'services';
  const popularItems = POPULAR_SOURCES[activeCollection] || [];
  const isSearching = searchQuery.trim().length > 0;

  // Explore Directory View
  return (
    <div className="min-h-screen bg-background page-fade-in" data-testid="explore-page">
      {isSearching ? (
        <div className="px-4 pt-3 pb-6 desktop-shell" data-testid="search-results">
          {searchGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="search-empty">
              <Search className="h-8 w-8 text-muted-foreground/40 mb-3" />
              <p className="text-sm font-semibold text-foreground">No matches</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[260px]">
                Nothing for “{searchQuery.trim()}”. Try a service, course, centre or cause.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {searchGroups.map((group) => (
                <div key={group.key}>
                  <h2 className="text-base md:text-lg font-bold text-[#0F172A] mb-3">{group.label}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-4">
                    {group.items.map((item) => (
                      <OfferingCard
                        key={item.id}
                        item={item}
                        isSaved={savedIds.has(item.id)}
                        onToggleSave={group.collection === 'category' ? undefined : handleToggleSave}
                        onClick={() => handleSearchResultClick(group.collection, item)}
                        testId={`search-result-${item.id}`}
                        saveTestId={`search-save-${item.id}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <>
      {/* Hub header — the category tiles are now the only way into a hub, so
          each hub needs its own way back to the Explore directory. */}
      {activeTopTab !== 'all' && (
        <div className="bg-white border-b border-border/60 px-4 py-3">
          <div className="desktop-shell flex items-center gap-3">
            <button
              type="button"
              data-testid="hub-back-button"
              onClick={() => handleTopTabClick('all')}
              aria-label="Back to Home"
            >
              <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
            </button>
            <span className="text-base font-bold text-[#0F172A]">{HUB_TITLES[activeTopTab]}</span>
          </div>
        </div>
      )}

      {activeTopTab === 'volunteer' ? (
        <VolunteerHub
          items={volunteerHubItems}
          savedIds={savedIds}
          onToggleSave={handleToggleSave}
          onSelectOpportunity={handleSelectVolunteerOpportunity}
          onExpressInterest={handleExpressInterest}
        />
      ) : activeTopTab === 'learn' ? (
        <LearnHub
          items={learnHubItems}
          savedIds={savedIds}
          onToggleSave={handleToggleSave}
          onSelectCourse={handleSelectLearnItem}
          onExpressInterest={handleExpressInterest}
        />
      ) : OFFERING_HUBS[activeTopTab] ? (
        <OfferingHub
          testId={`${activeTopTab}-hub`}
          items={detailCollections[activeTopTab]}
          savedIds={savedIds}
          onToggleSave={handleToggleSave}
          onSelect={(item) => { setSelectedOffering({ type: activeTopTab, item }); setChatEntryIsInbox(false); }}
          {...OFFERING_HUBS[activeTopTab]}
        />
      ) : activeTopTab === 'services' ? (
        <ServicesHub
          items={serviceHubItems}
          savedIds={savedIds}
          onToggleSave={handleToggleSave}
          onSelectService={handleServiceClick}
          onTrackApplication={handleTrackApplication}
        />
      ) : (
        <>
      {/* Categories — bare icons over a one-word label. The tiles carried a
          tinted card and a second line of copy before; both are gone because
          six boxed cards read as a form to fill in rather than a row of things
          to tap, and the subtitle only repeated what the hub says on arrival. */}
      <div className="px-4 pt-4 pb-1 desktop-shell" data-testid="explore-categories">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-y-4 gap-x-2">
          {exploreCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              data-testid={`explore-category-${cat.id}`}
              onClick={() => handleCategoryTileClick(cat)}
              className="flex flex-col items-center justify-start gap-2 text-center group"
            >
              <span className="relative">
                <img
                  src={cat.image}
                  alt=""
                  style={{ objectPosition: cat.imagePosition }}
                  className="w-14 h-14 md:w-16 md:h-16 rounded-2xl object-cover transition-transform duration-200 group-hover:scale-105"
                />
                {cat.badge && (
                  <span
                    data-testid={`explore-category-badge-${cat.id}`}
                    className="absolute -top-1.5 -right-2 px-1.5 py-[1px] rounded-full bg-[#F97316] text-white text-[9px] font-bold tracking-wide shadow-sm"
                  >
                    {cat.badge}
                  </span>
                )}
              </span>
              <span className="text-[13px] md:text-sm font-semibold leading-tight text-[#0F172A]">{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      <QuickShortcuts onOpenRewards={onOpenRewards} onScheduleGiving={onScheduleGiving} />

      {/* Hero carousel */}
      <div className="p-4 desktop-shell md:pt-6">
        <div
          className="relative rounded-2xl overflow-hidden h-[140px] md:h-[260px]"
          data-testid="hero-banner"
          style={{ backgroundColor: '#5EEAD4' }}
        >
          {heroSlides.map((slide, i) => (
            <div
              key={slide.id}
              className={`absolute inset-0 flex items-center transition-opacity duration-500 ${
                i === heroIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <div className="max-w-[54%] pl-5 md:pl-12 py-5 z-10">
                <h2 className="text-xl md:text-3xl font-extrabold text-[#0F172A] leading-tight">{slide.title}</h2>
                <p className="text-xs md:text-base text-[#0F172A]/70 mt-1.5 md:mt-3 leading-snug">{slide.subtitle}</p>
              </div>
              <img
                src={slide.image}
                alt=""
                className="absolute right-0 top-0 h-full w-[52%] object-cover object-center"
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="hero-dots">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              type="button"
              data-testid={`hero-dot-${i}`}
              onClick={() => setHeroIdx(i)}
              aria-label={`Show slide ${i + 1}`}
              aria-current={heroIdx === i ? 'true' : undefined}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                heroIdx === i ? 'w-5 bg-primary' : 'w-1.5 bg-primary/25'
              }`}
            />
          ))}
        </div>
      </div>

      <LearnMoreBanner
        item={learnHubItems.find((i) => i.id === 'fiqh-janazah-course')}
        onOpen={handleSelectLearnItem}
        onSeeAll={() => handleTopTabClick('learn')}
      />

      {/* Featured for You */}
      <div className="px-4 pt-5 pb-2 desktop-shell" data-testid="featured-for-you">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base md:text-lg font-bold text-[#0F172A]">Featured for You</h2>
          <button
            type="button"
            data-testid="featured-see-all"
            onClick={() => handleTopTabClick('learn')}
            className="text-xs font-semibold text-primary"
          >
            See All
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {featuredForYou.map((item) => {
            const source = item.linkType === 'learn'
              ? learnHubItems.find((i) => i.id === item.linkId)
              : volunteerHubItems.find((i) => i.id === item.linkId);
            if (!source) return null;
            return (
              <OfferingCard
                key={item.id}
                item={{ ...source, tag: item.tag, tagColor: item.tagColor }}
                isSaved={savedIds.has(source.id)}
                onToggleSave={() => handleToggleSave(source.id)}
                onClick={() => handleOpenFeatured(item)}
                testId={`featured-card-${item.id}`}
                saveTestId={`save-toggle-${item.id}`}
              />
            );
          })}
        </div>
      </div>

      {/* Popular Services */}
      <div className="px-4 pb-4 desktop-shell scroll-mt-4" data-testid="popular-services">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base md:text-lg font-bold text-[#0F172A]">Popular Services</h2>
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-3">
          {popularCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              data-testid={`category-pill-${cat.id}`}
              onClick={() => setActiveCategory(cat.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                activeCategory === cat.id ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-4" data-testid="popular-services-grid">
          {popularItems.map((item) => (
            <OfferingCard
              key={item.id}
              item={item}
              isSaved={savedIds.has(item.id)}
              onToggleSave={handleToggleSave}
              onClick={() => openPopularItem(activeCollection, item)}
              testId={`service-tile-${item.id}`}
              saveTestId={`service-save-${item.id}`}
            />
          ))}
        </div>
      </div>
      <div className="h-4" />
      </>
      )}
      </>
      )}
    </div>
  );
}
